import React, { useState, useEffect, useCallback } from 'react';
import Arms from './FOArms';
import FOReplay from './FOReplay';
import FORange from './FORange';
import FOLive from './FOLive';

const C = { grn:'#389e0d', red:'#cf1322', blue:'#0050b3', amber:'#ad4e00', gray:'#8c8c8c',
            purple:'#531dab', teal:'#08979c' };
const money = v => v == null ? '—' : (v < 0 ? '-' : '') + '\u20B9' + Math.abs(Math.round(v)).toLocaleString('en-IN');
const pct = (v, d=1) => v == null ? '—' : (v > 0 ? '+' : '') + v.toFixed(d) + '%';
const pnlColor = v => v == null ? 'var(--t2)' : v > 0 ? C.grn : v < 0 ? C.red : 'var(--t2)';
const box = { background:'var(--bg1)', border:'1px solid var(--bd)', borderRadius:8, padding:16, marginBottom:16 };
const th = { textAlign:'left', padding:'6px 10px', fontSize:11, color:'var(--t2)', fontWeight:600,
             textTransform:'uppercase', letterSpacing:0.3, borderBottom:'1px solid var(--bd)', whiteSpace:'nowrap' };
const td = { padding:'6px 10px', fontSize:13, borderBottom:'1px solid var(--bd)', whiteSpace:'nowrap' };
const rt = { ...td, textAlign:'right' };
const rth = { ...th, textAlign:'right' };

function Metric({ label, value, sub, color }) {
  return (
    <div style={{ background:'var(--bg2, var(--bg1))', border:'1px solid var(--bd)',
                  borderRadius:8, padding:'12px 14px', flex:'1 1 130px', minWidth:130 }}>
      <div style={{ fontSize:11, color:'var(--t2)', textTransform:'uppercase',
                    letterSpacing:0.3, marginBottom:4 }}>{label}</div>
      <div style={{ fontSize:20, fontWeight:600, color: color || 'var(--t1)' }}>{value}</div>
      {sub && <div style={{ fontSize:11, color:'var(--t2)', marginTop:2 }}>{sub}</div>}
    </div>
  );
}

function Payoff({ s }) {
  const p = s.payoff;
  if (!p || !p.length) return null;
  const W = 640, H = 170, pad = 30;
  const xs = p.map(z => z.spot), ys = p.map(z => z.pnl);
  const xmin = Math.min(...xs), xmax = Math.max(...xs);
  const ymin = Math.min(...ys, 0), ymax = Math.max(...ys, 0);
  const X = v => pad + (v - xmin)/(xmax - xmin)*(W - pad*2);
  const Y = v => H - pad - (v - ymin)/(ymax - ymin || 1)*(H - pad*2);
  const zero = Y(0);
  const posPath = p.map((z,i) => `${i?'L':'M'}${X(z.spot)},${Y(z.pnl)}`).join(' ');
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', height:'auto', display:'block' }}>
      {/* profit zone shading between breakevens */}
      {s.breakevens.length === 2 && (
        <rect x={X(s.breakevens[0])} y={pad-12} width={X(s.breakevens[1])-X(s.breakevens[0])}
              height={H-pad*2+24} fill={C.grn} opacity="0.07" />
      )}
      <line x1={pad} y1={zero} x2={W-pad} y2={zero} stroke="var(--bd)" strokeWidth="1" />
      <path d={posPath} fill="none" stroke={C.purple} strokeWidth="2" />
      {/* short strikes */}
      {s.short_strikes.map(k => (
        <g key={k}>
          <line x1={X(k)} y1={pad-10} x2={X(k)} y2={H-pad+6} stroke={C.amber}
                strokeWidth="1" strokeDasharray="3 3" />
          <text x={X(k)} y={pad-14} fontSize="9" fill={C.amber} textAnchor="middle">
            {k.toLocaleString('en-IN')}</text>
        </g>
      ))}
      {/* current spot */}
      {s.spot_now > xmin && s.spot_now < xmax && (
        <g>
          <line x1={X(s.spot_now)} y1={pad-10} x2={X(s.spot_now)} y2={H-pad+6}
                stroke={C.blue} strokeWidth="2" />
          <text x={X(s.spot_now)} y={H-pad+18} fontSize="10" fill={C.blue}
                textAnchor="middle" fontWeight="600">
            now {Math.round(s.spot_now).toLocaleString('en-IN')}</text>
        </g>
      )}
      <text x={pad} y={Y(s.max_profit)-4} fontSize="9" fill={C.grn}>
        max +{Math.round(s.max_profit).toLocaleString('en-IN')}</text>
      <text x={pad} y={H-pad-2} fontSize="9" fill={C.red}>
        max −{Math.round(s.max_loss).toLocaleString('en-IN')}</text>
    </svg>
  );
}



function Bar({ pct, label, good }) {
  const col = good ? C.grn : pct > 60 ? C.red : pct > 40 ? C.amber : C.grn;
  return (
    <div style={{ flex:'1 1 150px' }}>
      <div style={{ display:'flex', justifyContent:'space-between', fontSize:11.5,
                    color:'var(--t2)', marginBottom:3 }}>
        <span>{label}</span><strong style={{ color:col }}>{pct.toFixed(0)}%</strong>
      </div>
      <div style={{ height:7, background:'var(--bg2,#ececeb)', borderRadius:4, overflow:'hidden' }}>
        <div style={{ width:Math.min(100,pct)+'%', height:'100%', background:col }} />
      </div>
    </div>
  );
}

function Proposal({ p, onAct, busy }) {
  const [open, setOpen] = useState(true);
  const ctx = p.context && !p.context.error ? p.context : null;
  const held = ctx ? 100 - ctx.finished_outside : null;
  const poor = held != null && held < 55;
  return (
    <div style={{ ...box, borderLeft:'3px solid ' + (poor ? C.red : C.amber) }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline',
                    flexWrap:'wrap', gap:8 }}>
        <div>
          <span style={{ fontSize:15, fontWeight:600 }}>{p.name}</span>
          <span style={{ fontSize:12, color:'var(--t2)', marginLeft:10 }}>
            {p.underlying} · expiry {p.expiry} · {p.days_left}d · {p.lots} lot(s)
          </span>
        </div>
        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          {p.hours_left != null && (
            <span style={{ fontSize:11, color: p.hours_left < 4 ? C.red : 'var(--t2)' }}>
              lapses in {p.hours_left < 1 ? 'under an hour'
                         : p.hours_left.toFixed(0)+'h'}
            </span>
          )}
          <span style={{ fontSize:11, padding:'2px 8px', borderRadius:4, fontWeight:600,
                         background:(p.hedged?C.grn:C.red)+'18', color:p.hedged?C.grn:C.red }}>
            {p.hedged ? 'hedged — loss is capped' : 'NOT HEDGED — uncapped loss'}
          </span>
        </div>
      </div>

      <div style={{ display:'flex', gap:16, flexWrap:'wrap', fontSize:12.5,
                    margin:'10px 0', color:'var(--t2)' }}>
        <span>credit <strong style={{ color:C.grn }}>{money(p.credit)}</strong></span>
        <span>max loss <strong style={{ color:C.red }}>{money(p.max_loss)}</strong></span>
        <span>margin <strong style={{ color:'var(--t1)' }}>{money(p.margin)}</strong></span>
        <span>risk:reward <strong style={{ color: p.rr > 3 ? C.red : 'var(--t1)' }}>
          {p.rr}:1</strong></span>
        <span>return on margin <strong>{p.return_on_margin}%</strong></span>
        <span>band <strong>{p.breakevens.map(b=>Math.round(b).toLocaleString('en-IN')).join(' – ')}</strong></span>
      </div>

      {ctx && (
        <div style={{ background:'var(--bg2,#f6f6f4)', borderRadius:6, padding:'10px 12px',
                      marginBottom:10 }}>
          <div style={{ fontSize:12.5, fontWeight:600, marginBottom:8 }}>
            What history says about a band this wide
            <span style={{ fontWeight:400, color:'var(--t2)' }}>
              {' '}· {ctx.windows.toLocaleString('en-IN')} past {ctx.days}-day windows
            </span>
          </div>
          <div style={{ display:'flex', gap:16, flexWrap:'wrap', marginBottom:8 }}>
            <Bar pct={held} label="finished inside the band" good={held>=60} />
            <Bar pct={ctx.touched_either} label="touched a strike at some point" />
          </div>
          <div style={{ fontSize:12, color:'var(--t2)' }}>
            Typical {ctx.days}-day move is {ctx.distribution.avg_abs}%; this band is{' '}
            {Math.abs(ctx.band.lower_pct)}% below and {ctx.band.upper_pct}% above.{' '}
            {ctx.distribution.avg_abs > Math.min(Math.abs(ctx.band.lower_pct), ctx.band.upper_pct)
              ? <strong style={{ color:C.red }}>The band is narrower than the average move.</strong>
              : 'The band is wider than the average move.'}
          </div>
          <div style={{ fontSize:12, color:'var(--t2)', marginTop:5 }}>
            Range of outcomes: 5th percentile {ctx.distribution.p5}%, median{' '}
            {ctx.distribution.median}%, 95th {ctx.distribution.p95}%
          </div>
          {ctx.recent && ctx.recent.length > 0 && (
            <div style={{ marginTop:8 }}>
              <div style={{ fontSize:11.5, color:'var(--t2)', marginBottom:3 }}>
                the last {ctx.recent.length} windows (move, and how far it travelled):
              </div>
              <div style={{ display:'flex', gap:5, flexWrap:'wrap' }}>
                {ctx.recent.map((r,i) => {
                  const bad = (ctx.band.upper_pct && r.high >= ctx.band.upper_pct) ||
                              (ctx.band.lower_pct && r.low <= ctx.band.lower_pct);
                  return (
                    <span key={i} title={`${r.from_date} to ${r.to_date}: ${r.move}% (high ${r.high}%, low ${r.low}%)`}
                      style={{ fontSize:11, padding:'2px 6px', borderRadius:3,
                               background:(bad?C.red:C.grn)+'18', color:bad?C.red:C.grn }}>
                      {r.move > 0 ? '+' : ''}{r.move}%
                    </span>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      <button onClick={()=>setOpen(!open)} style={{ fontSize:12, padding:'3px 10px',
        borderRadius:5, border:'1px solid var(--bd)', background:'var(--bg1)',
        cursor:'pointer', marginBottom: open?8:0 }}>
        {open ? 'hide legs' : 'show legs'}
      </button>
      {open && (
        <table style={{ width:'100%', borderCollapse:'collapse', marginBottom:10 }}>
          <thead><tr>
            <th style={th}>Leg</th><th style={rth}>Strike</th><th style={th}>Type</th>
            <th style={rth}>Price</th><th style={rth}>Total</th>
          </tr></thead>
          <tbody>{p.legs.map((l,i)=>(
            <tr key={i}>
              <td style={{...td, color:l.side==='SELL'?C.grn:C.red, fontWeight:600}}>{l.side}</td>
              <td style={rt}>{l.strike.toLocaleString('en-IN')}</td>
              <td style={td}>{l.opt==='CE'?'call':'put'}</td>
              <td style={rt}>{l.price.toFixed(2)}</td>
              <td style={rt}>{money(l.price*p.units)}</td>
            </tr>
          ))}</tbody>
        </table>
      )}

      <div style={{ fontSize:12.5, color:'var(--t2)', marginBottom:4 }}>
        <strong style={{ color:'var(--t1)' }}>Fired because: </strong>{p.why}
      </div>
      <div style={{ fontSize:12.5, color:'var(--t2)', marginBottom:4 }}>
        <strong style={{ color:'var(--t1)' }}>Sizing: </strong>{p.sizing}
      </div>
      {p.exit_rule && (
        <div style={{ fontSize:12.5, color:'var(--t2)', marginBottom:10 }}>
          <strong style={{ color:'var(--t1)' }}>Exit rule: </strong>{p.exit_rule}
        </div>
      )}

      <div style={{ display:'flex', gap:8, alignItems:'center' }}>
        <button disabled={busy} onClick={()=>onAct(p.id,'take')} style={{
          fontSize:13, padding:'6px 18px', borderRadius:6, cursor:'pointer',
          border:'1px solid '+C.grn, background:C.grn+'12', color:C.grn, fontWeight:600 }}>
          Take it
        </button>
        <button disabled={busy} onClick={()=>onAct(p.id,'skip')} style={{
          fontSize:13, padding:'6px 18px', borderRadius:6, cursor:'pointer',
          border:'1px solid var(--bd)', background:'var(--bg1)', color:'var(--t2)' }}>
          Skip
        </button>
        <span style={{ fontSize:11.5, color:'var(--t2)', marginLeft:'auto' }}>
          paper only — nothing is sent to a broker
        </span>
      </div>
    </div>
  );
}

function Journey({ s }) {
  const h = s.history || [];
  if (h.length < 2) {
    return (
      <div style={{ fontSize:12.5, color:'var(--t2)', padding:'10px 0',
                    borderTop:'1px solid var(--bd)', marginTop:10 }}>
        The daily path appears here from tomorrow — one point per day showing what the
        position was worth and how close NIFTY came to your strikes.
      </div>
    );
  }
  const W = 640, H = 150, pad = 34;
  const ks = s.short_strikes;
  const spots = h.map(x => x.spot);
  const lo = Math.min(...spots, ...ks) * 0.995, hi = Math.max(...spots, ...ks) * 1.005;
  const X = i => pad + i/(h.length-1)*(W-pad*2);
  const Y = v => H - pad - (v-lo)/(hi-lo)*(H-pad*2);
  const path = h.map((x,i) => `${i?'L':'M'}${X(i)},${Y(x.spot)}`).join(' ');

  const pl = h.map(x => x.pnl);
  const pmin = Math.min(...pl, 0), pmax = Math.max(...pl, 0);
  const PY = v => H - pad - (v-pmin)/((pmax-pmin)||1)*(H-pad*2);
  const ppath = h.map((x,i) => `${i?'L':'M'}${X(i)},${PY(x.pnl)}`).join(' ');

  return (
    <div style={{ borderTop:'1px solid var(--bd)', marginTop:12, paddingTop:12 }}>
      <div style={{ fontSize:12.5, color:'var(--t2)', marginBottom:6 }}>
        Where NIFTY has gone, against your short strikes
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', height:'auto', display:'block' }}>
        {ks.length === 2 && (
          <rect x={pad} y={Y(ks[1])} width={W-pad*2} height={Math.abs(Y(ks[0])-Y(ks[1]))}
                fill={C.grn} opacity="0.16" />
        )}
        {ks.map(k => (
          <g key={k}>
            <line x1={pad} y1={Y(k)} x2={W-pad} y2={Y(k)} stroke={C.amber}
                  strokeWidth="1" strokeDasharray="4 3" />
            <text x={W-pad+2} y={Y(k)+3} fontSize="9" fill={C.amber}>
              {k.toLocaleString('en-IN')}</text>
          </g>
        ))}
        <path d={path} fill="none" stroke={C.blue} strokeWidth="2" />
        {h.map((x,i) => <circle key={i} cx={X(i)} cy={Y(x.spot)} r="2.5" fill={C.blue} />)}
        <text x={pad} y={H-8} fontSize="9" fill="var(--t2)">{h[0].date}</text>
        <text x={W-pad} y={H-8} fontSize="9" fill="var(--t2)" textAnchor="end">
          {h[h.length-1].date}</text>
      </svg>

      <div style={{ fontSize:12.5, color:'var(--t2)', margin:'10px 0 6px' }}>
        What the position has been worth
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', height:'auto', display:'block' }}>
        <rect x={pad} y={PY(Math.max(...pl, 0))} width={W-pad*2}
              height={Math.max(0, PY(0)-PY(Math.max(...pl, 0)))}
              fill={C.grn} opacity="0.08" />
        <rect x={pad} y={PY(0)} width={W-pad*2}
              height={Math.max(0, PY(Math.min(...pl, 0))-PY(0))}
              fill={C.red} opacity="0.08" />
        <line x1={pad} y1={PY(0)} x2={W-pad} y2={PY(0)} stroke="var(--t2)"
              strokeWidth="1.5" strokeDasharray="4 3" />
        <text x={W-pad+2} y={PY(0)+3} fontSize="9" fill="var(--t2)">0</text>
        <text x={W-pad+2} y={PY(Math.max(...pl,0))+8} fontSize="9" fill={C.grn}>
          {money(Math.max(...pl,0))}</text>
        <text x={W-pad+2} y={PY(Math.min(...pl,0))} fontSize="9" fill={C.red}>
          {money(Math.min(...pl,0))}</text>
        <path d={ppath} fill="none" stroke={C.purple} strokeWidth="2" />
        {h.map((x,i) => <circle key={i} cx={X(i)} cy={PY(x.pnl)} r="2.5"
                                fill={x.pnl >= 0 ? C.grn : C.red} />)}
        <text x={pad} y={PY(0)-5} fontSize="9" fill="var(--t2)">breakeven</text>
      </svg>

      <table style={{ width:'100%', borderCollapse:'collapse', marginTop:10 }}>
        <thead><tr>
          <th style={th}>Date</th><th style={rth}>NIFTY</th><th style={rth}>to nearest strike</th>
          <th style={rth}>P&amp;L</th><th style={rth}>days left</th>
        </tr></thead>
        <tbody>{[...h].reverse().map(x => (
          <tr key={x.date}>
            <td style={td}>{x.date}</td>
            <td style={rt}>{x.spot.toLocaleString('en-IN')}</td>
            <td style={{ ...rt, color: x.to_strike_pct < 0.5 ? C.red
                                     : x.to_strike_pct < 1 ? C.amber : 'var(--t1)' }}>
              {x.to_strike ? x.to_strike.toLocaleString('en-IN') + ' (' + x.to_strike_pct + '%)' : '—'}
            </td>
            <td style={{ ...rt, color:pnlColor(x.pnl) }}>{money(x.pnl)}</td>
            <td style={{ ...rt, color:'var(--t2)' }}>{x.days_left}</td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}


function CalcTable({ s }) {
  const t = s.table;
  if (!t || !t.length) return null;
  const labels = t[0].cells.map(c => c.label);
  return (
    <div style={{ borderTop:'1px solid var(--bd)', marginTop:12, paddingTop:12 }}>
      <div style={{ fontSize:13, fontWeight:600, marginBottom:4 }}>
        What happens at expiry, level by level
      </div>
      <div style={{ fontSize:12, color:'var(--t2)', marginBottom:8 }}>
        Each column is one leg. A sold option shows what you OWE if it finishes in the
        money; a bought one shows what you RECEIVE. Add them, add the credit you were
        paid, and the last column is your result.
      </div>
      <div style={{ overflowX:'auto' }}>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12.5 }}>
          <thead><tr>
            <th style={rth}>NIFTY at</th>
            {labels.map(l => <th key={l} style={rth}>{l}</th>)}
            <th style={rth}>legs total</th><th style={rth}>+ credit</th>
            <th style={{ ...rth, fontWeight:700 }}>result</th>
          </tr></thead>
          <tbody>
            {t.map((r,i) => (
              <tr key={i} style={{
                background: r.is_now ? C.blue+'12' : (r.inside ? C.grn+'0a' : C.red+'0a') }}>
                <td style={{ ...rt, fontWeight: r.is_now ? 700 : 400 }}>
                  {r.spot.toLocaleString('en-IN')}{r.is_now ? ' ←now' : ''}
                </td>
                {r.cells.map((c,j) => (
                  <td key={j} style={{ ...rt, color: c.value < 0 ? C.red
                                              : c.value > 0 ? C.grn : 'var(--t2)' }}>
                    {c.value === 0 ? '—' : money(c.value)}
                  </td>
                ))}
                <td style={{ ...rt, color:pnlColor(r.legs_total) }}>
                  {r.legs_total === 0 ? '—' : money(r.legs_total)}</td>
                <td style={{ ...rt, color:C.grn }}>{money(r.credit)}</td>
                <td style={{ ...rt, fontWeight:700, color:pnlColor(r.pnl) }}>{money(r.pnl)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ fontSize:12, color:'var(--t2)', marginTop:8 }}>
        Notice the bottom rows: once NIFTY passes your bought strike, the sold leg's loss
        keeps growing but the bought leg grows with it, so the result stops changing.
        That frozen number is your maximum loss.
      </div>
    </div>
  );
}

function Structure({ s }) {
  const [open, setOpen] = useState(false);
  const inBand = s.spot_now >= s.breakevens[0] && s.spot_now <= (s.breakevens[1] ?? 1e9);
  return (
    <div style={box}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline',
                    flexWrap:'wrap', gap:8, marginBottom:10 }}>
        <div>
          <span style={{ fontSize:16, fontWeight:600, textTransform:'capitalize' }}>
            {s.structure.replace(/_/g,' ')} <span style={{ color:'var(--t2)', fontWeight:400 }}>#{s.id}</span>
          </span>
          <span style={{ fontSize:12, color:'var(--t2)', marginLeft:10 }}>
            expiry {s.expiry} · {s.days_left} day{s.days_left === 1 ? '' : 's'} left
          </span>
        </div>
        <div style={{ display:'flex', gap:10, alignItems:'center' }}>
          <span style={{ fontSize:11, padding:'2px 8px', borderRadius:4, fontWeight:600,
                         background:(inBand?C.grn:C.red)+'18', color:inBand?C.grn:C.red }}>
            {inBand ? 'inside the profit band' : 'outside the band'}
          </span>
          <span style={{ fontSize:15, fontWeight:600, color:pnlColor(s.pnl) }}>{money(s.pnl)}</span>
        </div>
      </div>

      <p style={{ fontSize:13.5, margin:'0 0 8px' }}>{s.needs}</p>
      {s.status_text && (
        <p style={{ fontSize:13, margin:'0 0 12px', padding:'8px 12px', borderRadius:6,
                    background:'var(--bg2, #f6f6f4)', color:'var(--t1)' }}>
          {s.status_text}
        </p>
      )}

      <div style={{ marginBottom:12 }}>
        <div style={{ display:'flex', justifyContent:'space-between', fontSize:12,
                      color:'var(--t2)', marginBottom:4 }}>
          <span>Decay — how much of the credit you have earned so far</span>
          <span><strong style={{ color: s.pnl >= 0 ? C.grn : C.red }}>
            {money(s.pnl)}</strong> of {money(s.max_profit)}</span>
        </div>
        <div style={{ height:10, background:'var(--bg2, #ececeb)', borderRadius:5,
                      overflow:'hidden', position:'relative' }}>
          <div style={{ width: Math.max(0, Math.min(100, 100*s.pnl/s.max_profit)) + '%',
                        height:'100%', background: s.pnl >= 0 ? C.grn : C.red }} />
        </div>
        <div style={{ fontSize:11, color:'var(--t2)', marginTop:3 }}>
          Every day that passes with NIFTY inside the band moves this bar right.
          At expiry it reaches 100% and the credit is yours.
        </div>
      </div>

      <Payoff s={s} />

      <div style={{ display:'flex', gap:16, flexWrap:'wrap', fontSize:12.5,
                    margin:'6px 0 14px', color:'var(--t2)' }}>
        <span>breakevens <strong style={{ color:'var(--t1)' }}>
          {s.breakevens.map(b => Math.round(b).toLocaleString('en-IN')).join(' — ')}</strong></span>
        <span>credit <strong style={{ color:C.grn }}>{money(s.max_profit)}</strong></span>
        <span>max loss <strong style={{ color:C.red }}>{money(s.max_loss)}</strong></span>
        <span>margin <strong style={{ color:'var(--t1)' }}>{money(s.margin)}</strong></span>
        <span>return if it expires worthless <strong style={{ color:'var(--t1)' }}>
          {(100*s.max_profit/s.margin).toFixed(1)}%</strong></span>
        {s.closest_ever != null && (
          <span>closest NIFTY has come to a strike <strong style={{
            color: s.closest_ever < 100 ? C.red : s.closest_ever < 200 ? C.amber : 'var(--t1)' }}>
            {Math.round(s.closest_ever).toLocaleString('en-IN')} pts</strong></span>
        )}
      </div>

      <button onClick={() => setOpen(!open)} style={{
        fontSize:13.5, padding:'8px 20px', borderRadius:7, cursor:'pointer',
        border:'1px solid ' + C.purple, background: open ? C.purple+'12' : 'var(--bg1)',
        color:C.purple, fontWeight:600, marginBottom: open ? 12 : 4,
        display:'block' }}>
        {open ? '\u25be  Hide the four legs' : '\u25b8  Show the four legs of this trade'}
      </button>

      {open && (
        <>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead><tr>
              <th style={th}>Leg</th><th style={rth}>Strike</th><th style={th}>Type</th>
              <th style={rth}>Entry</th><th style={rth}>Now</th><th style={rth}>P&amp;L</th>
              <th style={rth}>Volume</th><th style={rth}>Open interest</th>
              <th style={th}>What it does</th>
            </tr></thead>
            <tbody>
              {s.legs.map((l,i) => (
                <tr key={i}>
                  <td style={{ ...td, color: l.side==='SELL' ? C.grn : C.red, fontWeight:600 }}>
                    {l.side}</td>
                  <td style={rt}>{l.strike.toLocaleString('en-IN')}</td>
                  <td style={td}>{l.opt === 'CE' ? 'call' : 'put'}</td>
                  <td style={rt}>{l.entry.toFixed(2)}</td>
                  <td style={rt}>{l.last.toFixed(2)}</td>
                  <td style={{ ...rt, color:pnlColor(l.pnl) }}>{money(l.pnl)}</td>
                  <td style={{ ...rt, color:'var(--t2)' }}>
                    {l.volume ? (l.volume/1e6).toFixed(1)+'M' : '—'}</td>
                  <td style={{ ...rt, color:'var(--t2)' }}>
                    {l.oi ? (l.oi/1e6).toFixed(2)+'M' : '—'}</td>
                  <td style={{ ...td, fontSize:12, color:'var(--t2)' }}>{l.role}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {s.thesis && (
            <p style={{ fontSize:12.5, color:'var(--t2)', margin:'12px 0 0',
                        paddingTop:10, borderTop:'1px solid var(--bd)' }}>
              <strong style={{ color:'var(--t1)' }}>Why this trade: </strong>{s.thesis}
            </p>
          )}
          <p style={{ fontSize:12.5, color:'var(--t2)', margin:'8px 0 0' }}>
            <strong style={{ color:'var(--t1)' }}>Exit rule: </strong>{s.exit_rule}
          </p>
        </>
      )}

      <CalcTable s={s} />
      <Journey s={s} />

    </div>
  );
}

function Glossary({ items }) {
  const [open, setOpen] = useState(false);
  if (!items) return null;
  return (
    <div style={box}>
      <button onClick={() => setOpen(!open)} style={{ fontSize:14, fontWeight:600,
        border:'none', background:'none', cursor:'pointer', padding:0, color:'var(--t1)' }}>
        {open ? '▾' : '▸'} What the terms mean
      </button>
      {open && (
        <div style={{ marginTop:12 }}>
          {items.map(g => (
            <div key={g.term} style={{ padding:'8px 0', borderTop:'1px solid var(--bd)' }}>
              <div style={{ fontSize:13, fontWeight:600 }}>{g.term}</div>
              <div style={{ fontSize:13, margin:'2px 0' }}>{g.plain}</div>
              <div style={{ fontSize:12.5, color:'var(--t2)' }}>{g.detail}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function Summary({ list, onPick, picked }) {
  if (!list || !list.length) return null;
  const tot = list.reduce((a,s) => ({
    margin: a.margin + s.margin, credit: a.credit + s.max_profit,
    maxloss: a.maxloss + s.max_loss, pnl: a.pnl + s.pnl }),
    { margin:0, credit:0, maxloss:0, pnl:0 });
  return (
    <div style={box}>
      <p style={{ fontSize:15, fontWeight:600, margin:'0 0 10px' }}>
        Open positions · {list.length}
      </p>
      <div style={{ overflowX:'auto' }}>
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead><tr>
            <th style={th}>#</th><th style={th}>Structure</th><th style={th}>Expiry</th>
            <th style={rth}>Days</th><th style={th}>Band it needs</th><th style={rth}>NIFTY</th>
            <th style={rth}>Credit</th><th style={rth}>Max loss</th><th style={rth}>Margin</th>
            <th style={rth}>P&amp;L</th><th style={rth}>Kept</th><th style={th}>Status</th>
          </tr></thead>
          <tbody>
            {list.map(s => {
              const ks = s.short_strikes;
              const inBand = s.spot_now >= s.breakevens[0] && s.spot_now <= (s.breakevens[1] ?? 1e9);
              const sel = picked === s.id;
              return (
                <tr key={s.id} onClick={() => onPick(sel ? null : s.id)}
                    style={{ cursor:'pointer', background: sel ? C.purple+'10' : 'transparent' }}>
                  <td style={td}>{s.id}</td>
                  <td style={{ ...td, textTransform:'capitalize' }}>
                    {s.structure.replace(/_/g,' ')}</td>
                  <td style={td}>{s.expiry}</td>
                  <td style={rt}>{s.days_left}</td>
                  <td style={td}>{ks.map(k => k.toLocaleString('en-IN')).join(' – ')}</td>
                  <td style={rt}>{Math.round(s.spot_now).toLocaleString('en-IN')}</td>
                  <td style={{ ...rt, color:C.grn }}>{money(s.max_profit)}</td>
                  <td style={{ ...rt, color:C.red }}>{money(s.max_loss)}</td>
                  <td style={rt}>{money(s.margin)}</td>
                  <td style={{ ...rt, color:pnlColor(s.pnl), fontWeight:600 }}>{money(s.pnl)}</td>
                  <td style={rt}>{s.max_profit ? (100*s.pnl/s.max_profit).toFixed(0)+'%' : '—'}</td>
                  <td style={td}>
                    <span style={{ fontSize:11, padding:'2px 8px', borderRadius:4, fontWeight:600,
                      background:(inBand?C.grn:C.red)+'18', color:inBand?C.grn:C.red }}>
                      {inBand ? 'in band' : 'outside'}</span>
                  </td>
                </tr>
              );
            })}
            <tr style={{ borderTop:'2px solid var(--bd)' }}>
              <td style={{ ...td, fontWeight:600 }} colSpan={6}>Total</td>
              <td style={{ ...rt, fontWeight:600, color:C.grn }}>{money(tot.credit)}</td>
              <td style={{ ...rt, fontWeight:600, color:C.red }}>{money(tot.maxloss)}</td>
              <td style={{ ...rt, fontWeight:600 }}>{money(tot.margin)}</td>
              <td style={{ ...rt, fontWeight:600, color:pnlColor(tot.pnl) }}>{money(tot.pnl)}</td>
              <td style={rt}></td><td style={td}></td>
            </tr>
          </tbody>
        </table>
      </div>
      <p style={{ fontSize:12, color:'var(--t2)', margin:'8px 0 0' }}>
        Click any row to open the full trade below.
      </p>
    </div>
  );
}


function BookRisk({ r }) {
  if (!r) return null;
  const heavy = r.cap_used_pct > 80;
  return (
    <div style={box}>
      <p style={{ fontSize:15, fontWeight:600, margin:'0 0 10px' }}>Risk across the book</p>
      <div style={{ display:'flex', gap:18, flexWrap:'wrap', fontSize:12.5, marginBottom:10 }}>
        <span style={{ color:'var(--t2)' }}>margin <strong style={{ color: heavy?C.red:'var(--t1)' }}>
          {money(r.margin_used)}</strong> of {money(r.margin_cap)} ({r.cap_used_pct}% of cap)</span>
        <span style={{ color:'var(--t2)' }}>credit at stake <strong style={{ color:C.grn }}>
          {money(r.total_credit)}</strong></span>
        <span style={{ color:'var(--t2)' }}>if everything fails <strong style={{ color:C.red }}>
          {money(r.total_max_loss)}</strong> ({r.max_loss_pct_capital}% of capital)</span>
        <span style={{ color:'var(--t2)' }}>reward per 1 of risk <strong>{r.reward_to_risk}</strong></span>
        {r.unhedged_count > 0 && <span style={{ color:C.red, fontWeight:600 }}>
          {r.unhedged_count} unhedged</span>}
      </div>
      {Object.keys(r.by_underlying || {}).length > 0 && (
        <table style={{ width:'100%', borderCollapse:'collapse', marginBottom:8 }}>
          <thead><tr>
            <th style={th}>Underlying</th><th style={rth}>Positions</th>
            <th style={rth}>Margin</th><th style={rth}>Credit</th><th style={rth}>Max loss</th>
          </tr></thead>
          <tbody>{Object.entries(r.by_underlying).map(([u,b])=>(
            <tr key={u}>
              <td style={td}>{u}</td><td style={rt}>{b.n}</td>
              <td style={rt}>{money(b.margin)}</td>
              <td style={{...rt, color:C.grn}}>{money(b.credit)}</td>
              <td style={{...rt, color:C.red}}>{money(b.max_loss)}</td>
            </tr>
          ))}</tbody>
        </table>
      )}
      {r.correlation_note && (
        <p style={{ fontSize:12.5, color:C.amber, margin:0 }}>⚠ {r.correlation_note}</p>
      )}
    </div>
  );
}

function ChainView({ ch, onExpiry }) {
  if (!ch || ch.error) return <div style={box}>{ch?.error || 'No chain captured yet.'}</div>;
  return (
    <div style={box}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline',
                    flexWrap:'wrap', gap:8, marginBottom:10 }}>
        <p style={{ fontSize:15, fontWeight:600, margin:0 }}>
          {ch.underlying} chain · {ch.spot.toLocaleString('en-IN')}
        </p>
        <div style={{ display:'flex', gap:6, alignItems:'center' }}>
          <span style={{ fontSize:12, color:'var(--t2)' }}>expiry</span>
          <select value={ch.expiry} onChange={e=>onExpiry(e.target.value)}
            style={{ fontSize:12, padding:'3px 8px', borderRadius:5,
                     border:'1px solid var(--bd)', background:'var(--bg1)' }}>
            {ch.expiries.map(e=><option key={e} value={e}>{e}</option>)}
          </select>
          <span style={{ fontSize:12, color:'var(--t2)' }}>{ch.days}d · lot {ch.lot}</span>
        </div>
      </div>
      <div style={{ overflowX:'auto' }}>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12.5 }}>
          <thead><tr>
            <th style={rth}>OI</th><th style={rth}>Vol</th><th style={rth}>CALL</th>
            <th style={{...th, textAlign:'center'}}>Strike</th>
            <th style={rth}>PUT</th><th style={rth}>Vol</th><th style={rth}>OI</th>
            <th style={rth}>from spot</th>
          </tr></thead>
          <tbody>
            {ch.rows.map(r=>(
              <tr key={r.strike} style={{ background: r.atm ? C.blue+'10' : 'transparent' }}>
                <td style={{...rt, color:'var(--t2)'}}>{r.ce?.oi ? (r.ce.oi/1e6).toFixed(1)+'M' : '—'}</td>
                <td style={{...rt, color:'var(--t2)'}}>{r.ce?.volume ? (r.ce.volume/1e6).toFixed(1)+'M' : '—'}</td>
                <td style={{...rt, fontWeight: r.dist_pct>0?400:600}}>{r.ce?.ltp?.toFixed(2) ?? '—'}</td>
                <td style={{...td, textAlign:'center', fontWeight:600}}>
                  {r.strike.toLocaleString('en-IN')}{r.atm?' •':''}</td>
                <td style={{...rt, fontWeight: r.dist_pct<0?400:600}}>{r.pe?.ltp?.toFixed(2) ?? '—'}</td>
                <td style={{...rt, color:'var(--t2)'}}>{r.pe?.volume ? (r.pe.volume/1e6).toFixed(1)+'M' : '—'}</td>
                <td style={{...rt, color:'var(--t2)'}}>{r.pe?.oi ? (r.pe.oi/1e6).toFixed(1)+'M' : '—'}</td>
                <td style={{...rt, color: Math.abs(r.dist_pct)<1?'var(--t1)':'var(--t2)'}}>
                  {r.dist_pct>0?'+':''}{r.dist_pct}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p style={{ fontSize:11.5, color:'var(--t2)', margin:'8px 0 0' }}>
        Calls get cheaper going down the page (higher strikes are less likely to be reached);
        puts get cheaper going up. The highlighted row is at the money.
      </p>
    </div>
  );
}

function Perf({ d }) {
  if (!d) return null;
  return (
    <div>
      <div style={box}>
        <p style={{ fontSize:15, fontWeight:600, margin:'0 0 10px' }}>By strategy</p>
        {d.by_strategy.length === 0
          ? <p style={{ fontSize:13, color:'var(--t2)', margin:0 }}>Nothing closed yet.</p>
          : <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead><tr><th style={th}>Strategy</th><th style={rth}>Closed</th>
                <th style={rth}>Wins</th><th style={rth}>P&amp;L</th><th style={rth}>on margin</th>
              </tr></thead>
              <tbody>{d.by_strategy.map(s=>(
                <tr key={s.name}><td style={td}>{s.name}</td><td style={rt}>{s.n}</td>
                  <td style={rt}>{s.wins}/{s.n}</td>
                  <td style={{...rt, color:pnlColor(s.pnl)}}>{money(s.pnl)}</td>
                  <td style={{...rt, color:pnlColor(s.avg_pct_margin)}}>{pct(s.avg_pct_margin)}</td>
                </tr>
              ))}</tbody>
            </table>}
      </div>
      <div style={{ display:'flex', gap:14, flexWrap:'wrap' }}>
        <div style={{ ...box, flex:'1 1 280px' }}>
          <p style={{ fontSize:15, fontWeight:600, margin:'0 0 10px' }}>Month by month</p>
          {d.monthly.length===0 ? <p style={{fontSize:13,color:'var(--t2)',margin:0}}>No history yet.</p>
            : <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <tbody>{d.monthly.map(m=>(
                  <tr key={m.month}><td style={td}>{m.month}</td><td style={rt}>{m.n}</td>
                    <td style={{...rt, color:pnlColor(m.pnl)}}>{money(m.pnl)}</td></tr>
                ))}</tbody></table>}
        </div>
        <div style={{ ...box, flex:'1 1 280px' }}>
          <p style={{ fontSize:15, fontWeight:600, margin:'0 0 10px' }}>Year by year</p>
          {d.yearly.length===0 ? <p style={{fontSize:13,color:'var(--t2)',margin:0}}>No history yet.</p>
            : <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <tbody>{d.yearly.map(y=>(
                  <tr key={y.year}><td style={td}>{y.year}</td><td style={rt}>{y.n}</td>
                    <td style={{...rt, color:pnlColor(y.pnl)}}>{money(y.pnl)}</td></tr>
                ))}</tbody></table>}
        </div>
      </div>
      <div style={box}>
        <p style={{ fontSize:15, fontWeight:600, margin:'0 0 4px' }}>Considered and refused</p>
        <p style={{ fontSize:12, color:'var(--t2)', margin:'0 0 10px' }}>
          What the rules turned down, and why — as important as what they took.
        </p>
        {d.refusals.map((r,i)=>(
          <div key={i} style={{ padding:'6px 0', borderTop:'1px solid var(--bd)', fontSize:12.5 }}>
            <span style={{ color:'var(--t2)' }}>{r.date}</span>{' · '}
            <strong>{r.name || '—'}</strong>{' · '}
            <span style={{ color:'var(--t2)' }}>{r.detail || r.reason}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function FOBook() {
  const [sub, setSub] = useState('options');
  const [view, setView] = useState('positions');
  const [ov, setOv] = useState(null);
  const [str, setStr] = useState(null);
  const [closed, setClosed] = useState(null);
  const [props, setProps] = useState(null);
  const [risk, setRisk] = useState(null);
  const [chain, setChain] = useState(null);
  const [perf, setPerf] = useState(null);
  const [arms, setArms] = useState(null);
  const [chainUnd, setChainUnd] = useState('NIFTY');
  const [chainExp, setChainExp] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setErr(null);
    try {
      setOv(await (await fetch('/fo/overview')).json());
      setStr(await (await fetch('/fo/structures?status=OPEN')).json());
      setClosed(await (await fetch('/fo/structures?status=CLOSED')).json());
      const pr = await (await fetch('/fo/proposals')).json();
      setProps(pr);
      const ids = (pr.proposals||[]).map(x=>x.id).join(',');
      setRisk(await (await fetch('/fo/book_risk?include='+ids)).json());
      setPerf(await (await fetch('/fo/performance')).json());
      try { setArms(await (await fetch('/fo/arms')).json()); } catch (e) { setArms(null); }
    } catch (e) { setErr(String(e)); }
  }, []);

  const loadChain = useCallback(async (und, exp) => {
    try {
      const q = '/fo/chain?underlying='+und + (exp ? '&expiry='+exp : '');
      setChain(await (await fetch(q)).json());
    } catch (e) { setErr(String(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (view==='chain') loadChain(chainUnd, chainExp); },
            [view, chainUnd, chainExp, loadChain]);

  const [actMsg, setActMsg] = useState(null);
  const act = async (id, what) => {
    setBusy(true); setActMsg(null);
    try {
      // 2026-10-12: the response was never inspected, so a refused action —
      // an expired proposal, most often — reloaded the page unchanged and the
      // button looked broken. Say what happened.
      const r = await fetch(`/fo/proposal/${id}/${what}`, { method:'POST' });
      const j = await r.json().catch(() => null);
      if (j && j.ok === false) setActMsg(j.error || 'the broker refused that');
      await load();
    } catch (e) { setErr(String(e)); }
    setBusy(false);
  };

  if (err) return <div style={{ ...box, color:C.red }}>Error: {err}</div>;
  if (!ov) return <div style={{ color:'var(--t2)', padding:20 }}>Loading…</div>;

  const nProps = props?.proposals?.length || 0;
  const ActBanner = () => actMsg ? (
    <div style={{ ...box, borderLeft:`3px solid ${C.red}`, color:C.red,
                  fontSize:13, padding:'10px 14px', marginBottom:12 }}>
      {actMsg}
    </div>) : null;
  const VIEWS = [['positions','Positions'], ['proposals', `Proposals${nProps?' ('+nProps+')':''}`],
                 ['chain','Option chain'], ['performance','Performance'], ['replay','Replay']];

  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:12, flexWrap:'wrap' }}>
        <h2 style={{ margin:0, fontSize:20 }}>F&amp;O paper book</h2>
        <span style={{ fontSize:11, padding:'2px 10px', borderRadius:4, fontWeight:600,
                       background:C.amber+'18', color:C.amber }}>
          PAPER — simulated, no real orders
        </span>
        <span style={{ fontSize:12, color:'var(--t2)' }}>{money(ov.capital)} notional</span>
        <button onClick={load} style={{ marginLeft:'auto', fontSize:12, padding:'4px 12px',
          borderRadius:6, border:'1px solid var(--bd)', background:'var(--bg1)', cursor:'pointer' }}>
          Refresh
        </button>
      </div>

      <div style={{ display:'flex', gap:4, marginBottom:12 }}>
        {[['options','Options'],['futures','Futures']].map(([k,l])=>(
          <button key={k} onClick={()=>setSub(k)} style={{
            fontSize:13, padding:'6px 18px', borderRadius:'6px 6px 0 0', cursor:'pointer',
            border:'1px solid '+(sub===k?C.amber:'var(--bd)'),
            borderBottom: sub===k ? '2px solid '+C.amber : '1px solid var(--bd)',
            background: sub===k ? C.amber+'10' : 'var(--bg1)',
            color: sub===k ? C.amber : 'var(--t1)', fontWeight: sub===k?600:400 }}>{l}</button>
        ))}
      </div>

      <div style={{ display:'flex', gap:12, flexWrap:'wrap', marginBottom:14 }}>
        <Metric label="Equity" value={money(ov.equity)} sub={money(ov.realised)+' realised'} />
        <Metric label="Open P&L" value={money(ov.open_pnl)} color={pnlColor(ov.open_pnl)}
                sub={ov.open_structures+' structure(s)'} />
        <Metric label="Margin used" value={money(ov.margin_used)}
                sub={ov.margin_pct.toFixed(1)+'% · cap '+money(ov.margin_cap)}
                color={ov.margin_used>ov.margin_cap?C.red:null} />
        <Metric label="Cash free" value={money(ov.capital-ov.margin_used)} />
        <Metric label="Max loss open" value={money(ov.max_loss_open)}
                sub={((100*ov.max_loss_open)/ov.capital).toFixed(1)+'% of book'} />
        <Metric label="India VIX" value={ov.vix?ov.vix.toFixed(1):'—'}
                sub={ov.vix_pct!=null ? ov.vix_pct.toFixed(0)+'th pct · '+
                     (ov.vix_pct>75?'elevated':ov.vix_pct>40?'normal':'calm') : ''} />
      </div>

      {sub === 'futures' ? (
        <div style={box}>
          <p style={{ fontSize:15, fontWeight:600, margin:'0 0 6px' }}>Futures</p>
          <p style={{ fontSize:13, color:'var(--t2)', margin:0 }}>
            Not built yet. Futures are delta-one — no strike, no premium, no decay; you post
            margin and the position moves point for point with the index. Testing showed the
            trend rule adds little on NIFTY 50 (it does not trend enough) and the basis costs
            about 4.3% a year. This tab will hold positions, basis and roll tracking when built.
          </p>
        </div>
      ) : (
        <>
          <div style={{ display:'flex', gap:4, marginBottom:14, flexWrap:'wrap' }}>
            {VIEWS.map(([k,l])=>(
              <button key={k} onClick={()=>setView(k)} style={{
                fontSize:12.5, padding:'5px 14px', borderRadius:6, cursor:'pointer',
                border:'1px solid '+(view===k?C.purple:'var(--bd)'),
                background: view===k?C.purple+'12':'var(--bg1)',
                color: view===k?C.purple:'var(--t1)', fontWeight: view===k?600:400 }}>{l}</button>
            ))}
          </div>

          {view==='proposals' && (
            <>
              <ActBanner />
              <BookRisk r={risk} />
              {nProps===0 && <div style={box}>
                <p style={{ fontSize:14, margin:0 }}>Nothing proposed right now.</p>
                <p style={{ fontSize:12.5, color:'var(--t2)', margin:'6px 0 0' }}>
                  The evaluator runs after the close each day. Proposals lapse if not
                  acted on within 24 hours, because a trade priced on yesterday's chain
                  is not the trade it appears to be. Strategies whose conditions did not
                  fire are listed under Performance, with the reason.
                </p></div>}
              {props?.proposals?.map(p=>(
                <Proposal key={p.id} p={p} onAct={act} busy={busy} />
              ))}
            </>
          )}

          {view==='positions' && (
            <>
              <FOLive />
              <FORange />
              {arms && <Arms list={arms.arms} />}
              {str && <Summary list={str.structures} onPick={()=>{}} picked={null} />}
              {str && str.structures.map(s => <Structure key={s.id} s={s} />)}
              {closed && closed.structures.length>0 && (
                <div style={box}>
                  <p style={{ fontSize:15, fontWeight:600, margin:'0 0 10px' }}>Closed</p>
                  <table style={{ width:'100%', borderCollapse:'collapse' }}>
                    <thead><tr><th style={th}>Structure</th><th style={th}>Expiry</th>
                      <th style={rth}>Credit</th><th style={rth}>P&amp;L</th>
                      <th style={rth}>on margin</th><th style={th}>Outcome</th></tr></thead>
                    <tbody>{closed.structures.map(s=>(
                      <tr key={s.id}>
                        <td style={td}>{s.structure.replace(/_/g,' ')} #{s.id}</td>
                        <td style={td}>{s.expiry}</td>
                        <td style={rt}>{money(s.max_profit)}</td>
                        <td style={{...rt, color:pnlColor(s.pnl)}}>{money(s.pnl)}</td>
                        <td style={{...rt, color:pnlColor(s.pnl)}}>{pct(s.pnl_pct_margin)}</td>
                        <td style={{...td, fontSize:12, color:'var(--t2)'}}>{s.outcome}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              )}
            </>
          )}

          {view==='chain' && (
            <>
              <div style={{ display:'flex', gap:6, marginBottom:10, flexWrap:'wrap' }}>
                {['NIFTY','BANKNIFTY','FINNIFTY','MIDCPNIFTY'].map(u=>(
                  <button key={u} onClick={()=>{setChainUnd(u); setChainExp(null);}} style={{
                    fontSize:12, padding:'4px 12px', borderRadius:5, cursor:'pointer',
                    border:'1px solid '+(chainUnd===u?C.teal:'var(--bd)'),
                    background: chainUnd===u?C.teal+'12':'var(--bg1)',
                    color: chainUnd===u?C.teal:'var(--t1)' }}>{u}</button>
                ))}
              </div>
              <ChainView ch={chain} onExpiry={setChainExp} />
            </>
          )}

              {view==='replay' && <FOReplay />}
          {view==='performance' && <Perf d={perf} />}
        </>
      )}

      <Glossary items={ov.glossary} />
    </div>
  );
}
